import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import api from '../../services/api';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  // Use URLSearchParams directly instead of the hook
  const email = new URLSearchParams(window.location.search).get('email');
  
  const [status, setStatus] = useState({
    type: null, // 'success', 'error', or 'loading'
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Validation schema
  const ResetPasswordSchema = Yup.object().shape({
    password: Yup.string()
      .required('Password is required')
      .min(6, 'Password must be at least 6 characters'),
    password_confirmation: Yup.string()
      .required('Please confirm your password')
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
  });

  // Verify token validity
  useEffect(() => {
    const verifyToken = async () => {
      if (!token || !email) {
        setStatus({
          type: 'error',
          message: 'Invalid password reset link. Please request a new one.'
        });
        setIsTokenValid(false);
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.post('/auth/verify-reset-token', {
          token,
          email
        });

        if (response.data.success) {
          setIsTokenValid(true);
        } else {
          setStatus({
            type: 'error',
            message: 'This password reset link has expired or is invalid. Please request a new one.'
          });
          setIsTokenValid(false);
        }
      } catch (error) {
        setStatus({
          type: 'error',
          message: 'This password reset link has expired or is invalid. Please request a new one.'
        });
        setIsTokenValid(false);
        console.error('Token verification error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    verifyToken();
  }, [token, email]);

  // Handle form submission
  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    setStatus({ type: null, message: '' });

    try {
      const response = await api.post('/auth/reset-password', {
        token,
        email,
        password: values.password,
        password_confirmation: values.password_confirmation
      });

      if (response.data.success) {
        setStatus({
          type: 'success',
          message: 'Your password has been reset successfully!'
        });
        
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Your password has been reset successfully. You can now log in with your new password.' 
            } 
          });
        }, 3000);
      } else {
        setStatus({
          type: 'error',
          message: response.data.message || 'Failed to reset password. Please try again.'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.response?.data?.message || 'An unexpected error occurred. Please try again.'
      });
      console.error('Reset password error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <svg className="animate-spin mx-auto h-10 w-10 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <h2 className="mt-6 text-center text-xl font-medium text-gray-900">
              Verifying your reset link...
            </h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/">
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            Teem <span className="text-primary">Wildlife Wonders</span>
          </h2>
        </Link>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Reset your password
        </h2>
        {isTokenValid && (
          <p className="mt-2 text-center text-sm text-gray-600">
            Please enter your new password below.
          </p>
        )}
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {status.type && (
            <div className={`mb-4 ${
              status.type === 'success' ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
            } border-l-4 p-4`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {status.type === 'success' ? (
                    <svg className="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="ml-3">
                  <p className={`text-sm ${
                    status.type === 'success' ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {status.message}
                  </p>
                </div>
              </div>
            </div>
          )}

          {isTokenValid ? (
            <Formik
              initialValues={{ password: '', password_confirmation: '' }}
              validationSchema={ResetPasswordSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched }) => (
                <Form className="space-y-6">
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      New Password
                    </label>
                    <div className="mt-1">
                      <Field
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="new-password"
                        className={`appearance-none block w-full px-3 py-2 border ${
                          errors.password && touched.password ? 'border-red-500' : 'border-gray-300'
                        } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
                      />
                      <ErrorMessage name="password" component="p" className="mt-2 text-sm text-red-600" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700">
                      Confirm New Password
                    </label>
                    <div className="mt-1">
                      <Field
                        id="password_confirmation"
                        name="password_confirmation"
                        type="password"
                        autoComplete="new-password"
                        className={`appearance-none block w-full px-3 py-2 border ${
                          errors.password_confirmation && touched.password_confirmation ? 'border-red-500' : 'border-gray-300'
                        } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
                      />
                      <ErrorMessage name="password_confirmation" component="p" className="mt-2 text-sm text-red-600" />
                    </div>
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      {isSubmitting ? (
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : null}
                      Reset Password
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-600 mb-4">
                {status.message || 'Invalid or expired password reset link.'}
              </p>
              <Link
                to="/forgot-password"
                className="inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Request New Reset Link
              </Link>
            </div>
          )}

          <div className="mt-6 text-center">
            <div className="text-sm">
              <Link to="/login" className="font-medium text-primary hover:text-primary-dark">
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
